<script setup lang="ts">
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import TripCardSection from './TripCardSection.vue';
import Bolt from '@files/bolt.svg?component';
import type TripPackage from '@itinerary/trip/TripPackage';
import useAutoReserveModal from '@composable/useAutoReserveModal';

const autoReserveStore = useAutoReserveStore();
const route = useRoute();
// const layoutStore = useLayoutStore();
const rootEl = ref<HTMLDivElement>();
// const itineraryStore = useItineraryStore();
// const { reservableList } = storeToRefs(itineraryStore);
const currentModule = useCurrentItineraryModule();
const { autoReservableList, autoReserveOnly, autoReservePriceOnly } =
    moduleToRefs(currentModule.value);
const { openAutoReserveModal } = useAutoReserveModal();
const openModal = (pkg?: TripPackage) => {
    if (pkg) {
        autoReserveStore.preSelectPackage = pkg;
        autoReserveStore.selectedPackages = [pkg];
    }
    if (autoReservePriceOnly.value) {
        autoReserveStore.showAll = true;
    }
    openAutoReserveModal('auto-reserve-card-click');
};
const isExpanded = ref(false);
const hasExpand = computed(() => autoReservableList.value.length > 8);

const shownTrips = computed(() =>
    isExpanded.value
        ? autoReservableList.value
        : autoReservableList.value.slice(0, 8)
);

const collapseTitle = computed(() =>
    isExpanded.value ? 'نمایش کمتر' : 'مشاهده همه سفرهای تکمیل شده'
);

onMounted(() => {
    if (route.query.autoReserveModal) {
        openAutoReserveModal('auto-opened-modal');
    }
    // if (reservableList.value.length <= 3) {
    //     const { complete, next } = layoutStore.capacityTrackTour;
    //     layoutStore.capacityTourAddStep({
    //         step: {
    //             id: layoutStore.autoReserveStepKey,
    //             title: 'موجود شد رزرو کن',
    //             text: 'با فعال کردن این گزینه، در صورت باز شدن موجودی یا کنسلی توسط مسافرین دیگر، سفر به‌طور خودکار برای شما رزرو می‌شود.',
    //             attachTo: {
    //                 element: rootEl.value!,
    //                 on: 'bottom',
    //             },
    //             buttons: [
    //                 {
    //                     text: 'تمام',
    //                     action: complete,
    //                 },
    //                 {
    //                     text: 'بعدی',
    //                     action: next,
    //                 },
    //             ],
    //         },
    //         index: 0,
    //     });
    // }
});

// onClickOutside(rootEl, evt => {
//     const target = evt.target as SVGPathElement;
//     if (
//         layoutStore.capacityTrackTour.getCurrentStep()?.id ===
//             layoutStore.autoReserveStepKey &&
//         target?.parentElement?.classList?.contains(
//             'shepherd-modal-overlay-container'
//         )
//     ) {
//         layoutStore.capacityTrackTour.complete();
//     }
// });
</script>
<template>
    <div>
        <div id="auto-reserve" class="the-auto-reserve-card-root">
            <div ref="rootEl" class="header" :class="{ only: autoReserveOnly }">
                <mr-typography
                    v-if="autoReserveOnly"
                    :size="{ desktop: 'x-large', mobile: 'large' }"
                    weight="700"
                >
                    در حال حاضر ظرفیت خالی موجود نیست.
                </mr-typography>
                <div class="header-content">
                    <Bolt class="bolt" />
                    <div class="texts">
                        <template v-if="!autoReserveOnly">
                            <mr-typography
                                :size="{ desktop: 'x-large', mobile: 'large' }"
                                weight="700"
                            >
                                {{ autoReservePriceOnly ? 'ارزان' : 'موجود' }}
                                شد رزرو کن!
                                <mr-typography
                                    type="span"
                                    size="small"
                                    weight="500"
                                >
                                    (جدید)
                                </mr-typography>
                            </mr-typography>
                            <mr-typography
                                :size="{ desktop: 'large', mobile: 'medium' }"
                            >
                                در لحظه
                                {{ autoReservePriceOnly ? 'ارزان' : 'موجود' }}
                                شدن
                                {{
                                    autoReservePriceOnly
                                        ? `${autoReserveStore.reserveTypeInfo.ticketsOf} موجود`
                                        : 'ظرفیت خالی'
                                }}، رزرو خودکار انجام می‌شود.
                            </mr-typography>
                        </template>
                        <mr-typography v-else align="right">
                            با
                            <mr-typography weight="700" type="span">
                                «موجود شد رزرو کن»
                            </mr-typography>
                            در لحظه موجود شدن ظرفیت خالی بلیط به صورت خودکار
                            برای شما صادر می‌شود.
                        </mr-typography>
                    </div>
                </div>
                <mr-button class="button" @click="openModal()">
                    {{ autoReserveOnly ? 'موجود شد رزرو کن' : 'ادامه و رزرو' }}
                </mr-button>
            </div>
            <TripCardSection
                :list="shownTrips as TripPackage[]"
                @button-clicked="openModal"
            />
            <mr-button
                v-if="hasExpand"
                size="medium"
                variant="transparent"
                class="more"
                :left-icon="isExpanded ? faChevronUp : faChevronDown"
                @click="isExpanded = !isExpanded"
            >
                {{ collapseTitle }}
            </mr-button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.full-title {
    margin: 16px 0;
}
.the-auto-reserve-card-root {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 24px 32px 16px 32px;
    @include shadow;
    background-color: usePalette($p-yellow, $p-200);
    border-radius: 8px;
    margin-top: 8px;
    overflow: hidden;
    .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        .header-content {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            .bolt {
                flex-shrink: 0;
            }
            .body {
                display: flex;
                flex-direction: column;
                flex-grow: 1;
            }
        }

        .button {
            flex: 1;
            min-width: 120px;
            max-width: 206px;
        }
        &.only {
            flex-direction: column;
            .button {
                @include desktop-only {
                    align-self: center;
                    max-width: 360px;
                    width: 100%;
                }
            }
        }
        @include mobile-only {
            flex-direction: column;
            .button {
                align-self: stretch;
                max-width: unset;
            }
        }
    }
    .more {
        align-self: center;
    }
}
</style>
